import { useCallback, useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Avatar,
  Stack,
  CircularProgress,
  Alert,
} from "@mui/material";
import apiService from "../../../../../service/Api";
import { AxiosError } from "axios";
import { useNavigate } from "react-router-dom";

type CreatedBy = {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
};

type ContactRow = {
  id: number;
  name: string;
  phone: string | null;
  email: string | null;
  created_at: string;
  created_by: CreatedBy | null;
};

type WeeklyLatestContactsResponse = {
  week_start: string;
  week_end: string;
  count: number;
  results: ContactRow[];
};

function initialsFromName(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const a = parts[0]?.[0] ?? "C";
  const b = parts[1]?.[0] ?? parts[0]?.[1] ?? "";
  return (a + b).toUpperCase();
}

function prettyDate(iso: string) {
  const d = new Date(iso);
  return new Intl.DateTimeFormat(undefined, {
    weekday: "short",
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(d);
}

export default function RecentContactsByWeekWidget() {
  const [data, setData] = useState<WeeklyLatestContactsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const navigate = useNavigate();

  const fetchWeeklyLatest = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const res = await apiService.get<WeeklyLatestContactsResponse>(
        "/api/management/dashboard/contacts-weekly-latest/",
        {
          params: { week: "current", limit: 10 },
        },
      );

      setData(res.data);
    } catch (e) {
      if (e instanceof AxiosError) {
        const msg =
          e.response?.data?.detail ||
          e.message ||
          "Failed to load weekly contacts.";
        setError(msg);
      } else {
        setError("Failed to load weekly contacts.");
        console.error(e);
      }
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchWeeklyLatest();
  }, [fetchWeeklyLatest]);

  return (
    <Card sx={{ p: 2.5, borderRadius: 3, boxShadow: 0, height: "100%" }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
          mb: 2,
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight={800}>
            Recently created contacts
          </Typography>

          {data && (
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ mt: 1 }}
            >
              <Chip
                label={`${data.count} ${data.count === 1 ? "contact" : "contacts"}`}
                size="small"
              />
            </Stack>
          )}
        </Box>
      </Box>

      {/* States */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : !data || data.results.length === 0 ? (
        <Box sx={{ py: 6, textAlign: "center" }}>
          <Typography fontWeight={700}>No contacts found</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            No contacts were created this week.
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={2}>
          {data.results.map((c) => {
            const creatorName = c.created_by
              ? `${c.created_by.first_name ?? ""} ${c.created_by.last_name ?? ""}`.trim() ||
                c.created_by.email
              : "—";

            return (
              <Grid key={c.id} size={12}>
                <Card
                  variant="outlined"
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate(`/contacts/individual/view/${c.id}`)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      navigate(`/contacts/view/${c.id}`);
                    }
                  }}
                  sx={{
                    borderRadius: 3,
                    transition: "all .15s ease",
                    cursor: "pointer",
                    "&:hover": { transform: "translateY(-2px)", boxShadow: 2 },
                    "&:focus-visible": {
                      outline: "2px solid",
                      outlineColor: "primary.main",
                      outlineOffset: 2,
                    },
                  }}
                >
                  <CardContent>
                    <Stack
                      direction="row"
                      spacing={1.5}
                      alignItems="flex-start"
                    >
                      <Avatar
                        sx={{
                          bgcolor: "grey.200",
                          color: "text.primary",
                          fontWeight: 800,
                        }}
                      >
                        {initialsFromName(c.name || "Contact")}
                      </Avatar>

                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography fontWeight={800} noWrap>
                          {c.name || "Unnamed Contact"}
                        </Typography>

                        <Box sx={{ display: "flex", flexDirection: "column" }}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            noWrap
                          >
                            Created: <b>{prettyDate(c.created_at)}</b>
                          </Typography>

                          <Typography variant="caption" color="text.secondary">
                            Created By:<b>{creatorName}</b>
                          </Typography>
                        </Box>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}
    </Card>
  );
}
